import React, { useCallback, useEffect, useState } from 'react';
import { transcriptionService } from '../../services/api';
import { TranscriptionStats } from '../../types';
import { formatDateTime } from '../../utils/datetime';
import ScopeFilter, { ScopeSelection } from '../ScopeFilter';
import TranscriptionLanguages from './TranscriptionLanguages';

// Transcription usage: where the archive stands, what it has cost, and whether
// the results are any good.
//
// Billed time is what the API reported charging, not audio duration times a
// rate. The two genuinely differ — billing rounds up per request and one call
// becomes one request per non-silent channel — so a 16-second two-party call
// billed 26 seconds. Showing an estimate here would be confidently wrong.

const nf = new Intl.NumberFormat();

function duration(seconds: number): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m < 60 ? `${m}m ${s}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

// The states, in the order an operator reads them: what worked, what is
// waiting on a decision, what there was nothing to do about, what broke.
const STATUS_LABELS: { key: string; label: string; hint: string }[] = [
  { key: 'done', label: 'Transcribed', hint: 'Transcript written into the vCon and searchable' },
  { key: 'no_consent', label: 'Awaiting consent', hint: 'A party has not granted the transcription purpose. Not a failure.' },
  { key: 'empty', label: 'No speech', hint: 'Transcribed successfully; the engine recognised nothing' },
  { key: 'no_audio', label: 'No audio', hint: 'The vCon carries no audio to transcribe' },
  { key: 'failed', label: 'Failed', hint: 'The engine or the fetch errored; retried by the next scheduled run' },
];

const TranscriptionPanel: React.FC = () => {
  const [stats, setStats] = useState<TranscriptionStats | null>(null);
  const [scope, setScope] = useState<ScopeSelection>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Usage is a record of what happened; languages decide what happens next.
  // Two tabs rather than one long page, because they are read at different
  // times and only one of them is ever edited.
  const [tab, setTab] = useState<'usage' | 'languages'>('usage');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await transcriptionService.stats({ territory: scope.territory, domain: scope.domain }));
    } catch (e) {
      setError('Could not load transcription statistics');
    } finally {
      setLoading(false);
    }
  }, [scope.territory, scope.domain]);

  useEffect(() => { load(); }, [load]);


  const confidenceTotal = stats
    ? stats.confidence.high + stats.confidence.good + stats.confidence.fair + stats.confidence.low
    : 0;
  const peakDaily = stats ? Math.max(1, ...stats.daily.map(d => d.billed_seconds)) : 1;

  return (
    <div className="settings-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>Transcription</h2>
        <button type="button" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <nav className="settings-subnav">
        <button className={`nav-button ${tab === 'usage' ? 'active' : ''}`} onClick={() => setTab('usage')}>
          Usage
        </button>
        <button className={`nav-button ${tab === 'languages' ? 'active' : ''}`} onClick={() => setTab('languages')}>
          Languages
        </button>
      </nav>

      <ScopeFilter value={scope} onChange={setScope} />

      {error && <div className="error">{error}</div>}
      {loading && !stats && <div className="loading">Loading…</div>}

      {stats?.active_engine && (
        <p className="small" title="What the next transcription on this host will use">
          <strong>Engine on this host:</strong>{' '}
          {stats.active_engine.local
            ? <>
                {stats.active_engine.engine}
                {stats.active_engine.model && <> ({stats.active_engine.model})</>}
                {' '}— local; call audio does not leave this machine.{' '}
                {stats.active_engine.reachable
                  ? '✅ Server is answering.'
                  : <span style={{ color: '#c0392b' }}>⚠ Server is not answering — transcriptions will fail until it is started.</span>}
              </>
            : <>{stats.active_engine.engine} — cloud; audio is sent to Google for recognition.</>}
        </p>
      )}

      {stats && tab === 'languages' && (
        <TranscriptionLanguages stats={stats} onSaved={load} />
      )}

      {stats && tab === 'usage' && (
        <>
          <div className="status-cards">
            {STATUS_LABELS.map(s => {
              const row = stats.by_status[s.key];
              return (
                <div className="status-card" key={s.key} title={s.hint}>
                  <h4>{s.label}</h4>
                  <div className="status-card-value">{nf.format(row?.calls ?? 0)}</div>
                  {row && row.audio_seconds > 0 && (
                    <div className="status-card-detail">{duration(row.audio_seconds)} of audio</div>
                  )}
                </div>
              );
            })}
            <div className="status-card" title="Call recordings with no transcription attempt yet">
              <h4>Not yet attempted</h4>
              <div className="status-card-value">{nf.format(stats.never_attempted)}</div>
              {/* How long that backlog will take depends on there being a
                  nightly run at all. This instance is asked rather than
                  assumed: the run is a scheduled job that not every deployment
                  installs, and quoting a drain rate for a job that never fires
                  describes a queue that is really a standstill. */}
              <div className="status-card-detail">
                {!stats.nightly?.scheduled
                  ? 'No scheduled run here — transcribe from the vCons page'
                  : stats.nightly.kind === 'automatic'
                    ? stats.nightly.stale
                      ? '⚠ Automatic transcription has stopped'
                      : 'Transcribed automatically as calls arrive'
                  : stats.nightly.stale
                    ? '⚠ The nightly run has stopped'
                    : stats.nightly_limit !== null
                      ? stats.never_attempted <= stats.nightly_limit
                        ? 'Clears in one nightly run'
                        : `${Math.ceil(stats.never_attempted / stats.nightly_limit)} nightly runs at ${stats.nightly_limit}/run`
                      : 'Picked up by the nightly run'}
              </div>
            </div>
          </div>

          <h3>Billed usage</h3>
          <p className="small">
            {stats.active_engine?.local && 'Local recognition is not billed, so new work adds nothing here. '}
            What Speech-to-Text reported charging for, not the length of the
            recordings. Billing rounds up per request, and each non-silent channel
            of a call is its own request — so a two-party call can bill more than
            its own duration, and one with a silent side less.
          </p>
          <div className="status-cards">
            <div className="status-card">
              <h4>Billed audio</h4>
              <div className="status-card-value">{duration(stats.totals.billed_seconds)}</div>
              <div className="status-card-detail">
                across {nf.format(stats.totals.requests)} API request
                {stats.totals.requests === 1 ? '' : 's'}
              </div>
            </div>
            <div className="status-card">
              <h4>Audio processed</h4>
              <div className="status-card-value">{duration(stats.totals.audio_seconds)}</div>
              <div className="status-card-detail">{nf.format(stats.totals.chars)} characters of text</div>
            </div>
            <div className="status-card">
              <h4>Engine</h4>
              {stats.engines.length === 0 ? (
                <div className="status-card-value">—</div>
              ) : (
                stats.engines.map((e, i) => (
                  <div key={i}>
                    <div className="status-card-value" style={{ fontSize: '0.95rem' }}>{e.model}</div>
                    <div className="status-card-detail">
                      {e.engine} · {e.language} · {nf.format(e.n)} call{e.n === 1 ? '' : 's'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {stats.untracked_calls > 0 && (
            <p className="small" style={{ opacity: 0.8 }}>
              ⚠ {nf.format(stats.untracked_calls)} of these transcriptions ran before billed
              usage was recorded, so their billed time counts as zero above. The
              figures cover work done since; they are a floor, not a total.
            </p>
          )}

          <h3>Recognition quality</h3>
          <p className="small">
            The engine's own confidence. On 8&nbsp;kHz telephony a low score usually
            means the model could not make the audio out — worth checking before
            concluding that something was never said.
          </p>
          {confidenceTotal === 0 ? (
            <p className="small">Nothing transcribed yet.</p>
          ) : (
            <table className="wide-table" style={{ maxWidth: '520px' }}>
              <tbody>
                {([
                  ['high', 'High (90%+)'],
                  ['good', 'Good (70–89%)'],
                  ['fair', 'Fair (50–69%)'],
                  ['low', 'Low (under 50%)'],
                ] as const).map(([key, label]) => {
                  const n = stats.confidence[key];
                  const pct = Math.round((n / confidenceTotal) * 100);
                  return (
                    <tr key={key}>
                      <td style={{ width: '150px' }}>{label}</td>
                      <td style={{ width: '60px', textAlign: 'right' }}>{nf.format(n)}</td>
                      <td>
                        <div className="storage-bar" title={`${pct}%`}>
                          <div className="storage-bar-used" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                      <td style={{ width: '48px', textAlign: 'right' }} className="small">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {stats.blocked.length > 0 && (
            <>
              <h3>Waiting on consent</h3>
              <p className="small">
                These are not failures. Transcription is a separate consent purpose,
                and these recordings are held because a party has not granted it —
                grant it on the Consent page{stats.nightly?.scheduled && !stats.nightly.stale
                  ? stats.nightly.kind === 'automatic'
                    ? ' and they are picked up within 15 minutes.'
                    : ' and the next nightly run picks them up.'
                  : ', then transcribe them from the vCons page (there is no scheduled run here).'}
              </p>
              <div className="table-container">
                <table className="wide-table">
                  <thead><tr><th>Blocked by</th><th style={{ textAlign: 'right' }}>Calls</th></tr></thead>
                  <tbody>
                    {stats.blocked.map((b, i) => (
                      <tr key={i}>
                        <td className="wrap">{b.consent_note || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{nf.format(b.calls)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {stats.daily.length > 0 && (
            <>
              <h3>Usage over the last 30 days</h3>
              <p className="small">By the day the work was done, not the day of the call.</p>
              <div className="table-container">
                <table className="wide-table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th style={{ textAlign: 'right' }}>Calls</th>
                      <th style={{ textAlign: 'right' }}>Requests</th>
                      <th style={{ textAlign: 'right' }}>Billed</th>
                      <th style={{ width: '45%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.daily.map(d => (
                      <tr key={d.day}>
                        <td className="monospace small">{d.day}</td>
                        <td style={{ textAlign: 'right' }}>{nf.format(d.calls)}</td>
                        <td style={{ textAlign: 'right' }}>{nf.format(d.requests)}</td>
                        <td style={{ textAlign: 'right' }}>{duration(d.billed_seconds)}</td>
                        <td>
                          <div className="storage-bar">
                            <div className="storage-bar-used"
                                 style={{ width: `${Math.round((d.billed_seconds / peakDaily) * 100)}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h3>Recent activity</h3>
          <div className="table-container">
            <table className="wide-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Engine</th>
                  <th style={{ textAlign: 'right' }}>Ch</th>
                  <th style={{ textAlign: 'right' }}>Segments</th>
                  <th style={{ textAlign: 'right' }}>Chars</th>
                  <th style={{ textAlign: 'right' }}>Conf.</th>
                  <th style={{ textAlign: 'right' }}>Audio</th>
                  <th style={{ textAlign: 'right' }}>Billed</th>
                  <th style={{ textAlign: 'right' }}>Tries</th>
                  <th>vCon</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map(r => (
                  <tr key={r.vcon_uuid}>
                    <td className="small">{formatDateTime(r.completed_at || r.updated_at)}</td>
                    <td className="monospace small">{r.domain || '—'}</td>
                    <td className="small">
                      {r.status === 'done' ? '📝 done'
                        : r.status === 'no_consent' ? '🚫 consent'
                        : r.status === 'failed' ? <span style={{ color: '#c0392b' }}>⚠ failed</span>
                        : r.status}
                    </td>
                    <td className="small" title={r.model ?? undefined}>{r.engine ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.channels ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.segments || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.chars || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {r.audio_seconds ? duration(Number(r.audio_seconds)) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.billed_seconds ? duration(r.billed_seconds) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.attempts}</td>
                    <td className="monospace small">{r.vcon_uuid.slice(0, 8)}</td>
                  </tr>
                ))}
                {stats.recent.length === 0 && (
                  <tr><td colSpan={12}>Nothing transcribed yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TranscriptionPanel;
